import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router';
import { observer } from 'mobx-react';
import { css } from '@emotion/react';
import { useIntl } from 'react-intl';
import { BridgedButton as Button } from 'components/common/bridged-button';
import styled from 'types/theme.types';
import { legacyOnly, newDesignOnly } from 'constants/themes.constants';
import { Loader } from 'components/common/loader';
import ErrorContainer from 'components/common/error-container';
import { useHasOrganizations } from 'hooks/use-has-organization.hook';
import { ONBOARDING_ROUTE_PREFIX } from 'components/routes/routes';
import useStores from 'hooks/use-stores.hooks';

export const LOGIN_PAGE_TITLE_ID = 'navigation.login';

const LoginPage: React.FC = () => {
  const { authStore } = useStores();
  const {
    show,
    logout,
    isAuthenticated,
    getRedirectToUrl,
    isCallbackPage,
    handleUniversalLoginCallback,
    hasAuth0RedirectErrorInUrl,
    handleAuth0RedirectErrorFromUrl,
    redirectToSsoIfRequired
  } = authStore;
  const intl = useIntl();
  const { hasOrganizations, isLoadingOrganizations } = useHasOrganizations();
  const [auth0RedirectErrorMessage, setAuth0RedirectErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      if (hasAuth0RedirectErrorInUrl()) {
        if (!redirectToSsoIfRequired()) setAuth0RedirectErrorMessage(handleAuth0RedirectErrorFromUrl());
        return;
      }
      if (isCallbackPage()) {
        handleUniversalLoginCallback();
      } else if (authStore.lastAccessTokenIsValid) {
        // A design-preference reload can return here after the callback has already cleared its hash.
        void authStore.login().then(() => {
          if (!authStore.isAuthenticated) void show();
        });
      } else {
        show();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  if (!isAuthenticated && auth0RedirectErrorMessage) {
    return (
      <StyledErrorWrapper>
        <ErrorContainer message={auth0RedirectErrorMessage || ''} />
        <Button
          type="default"
          data-testid="back-to-login-button"
          onClick={() => {
            setAuth0RedirectErrorMessage(null);
            logout();
          }}>
          {intl.formatMessage({ id: 'login.back-to-login' })}
        </Button>
      </StyledErrorWrapper>
    );
  }

  if (isAuthenticated) {
    if (isLoadingOrganizations) {
      return <Loader />;
    }

    return <Navigate replace to={hasOrganizations ? getRedirectToUrl() : `${ONBOARDING_ROUTE_PREFIX}/welcome`} />;
  }

  return null;
};

export default observer(LoginPage);

const StyledErrorWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  ${legacyOnly(css`
    gap: 16px;
    padding: 24px;
  `)}

  ${newDesignOnly(
    theme => css`
      gap: ${theme.spacing.lg};
      padding: ${theme.padding.xxl};
    `
  )}
`;
